import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
  Chip,
  Divider,
  Paper,
  SelectChangeEvent,
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import { ReactNode } from 'react';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  Percent as PercentIcon,
  Money as MoneyIcon
} from '@mui/icons-material';
import * as paymentMethodService from 'services/paymentMethodService';
import { getAllAccounts, getActiveAccounts } from 'services/accountService';
import { getUsers } from 'services/userService';
import { getServiceProducts, ServiceProduct } from 'services/serviceProductService';
import { PaymentMethod } from '../../../types/paymentMethod';

export interface PaymentMethodFormProps {
  initialData?: PaymentMethod;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Account {
  _id: string;
  id?: string; // اضافة معرف بديل للتوافق مع الأنظمة المختلفة
  name: string;
  accountNumber?: string;
}

interface User {
  _id: string;
  name?: string;
  fullName?: string;  // Matching User model
  username?: string;  // Matching User model
  email?: string;
}

const PaymentMethodForm = ({
  initialData,
  onSuccess,
  onCancel
}: PaymentMethodFormProps): React.ReactNode => {
  const isEditing = !!initialData;
  
  // استخدام مرجع للعنصر المخفي
  const hiddenInputRef = React.useRef<HTMLInputElement>(null);
  
  // إضافة حالة منفصلة للحساب المحدد
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [isLoadingAccount, setIsLoadingAccount] = useState<boolean>(false);
  const [accountStatus, setAccountStatus] = useState<'loading' | 'loaded' | 'error' | 'none'>('none');
  
  // إضافة وظيفة مساعدة لجلب الحساب بناءً على المعرف
  const fetchAccountById = async (accountId: string) => {
    if (!accountId) return null;
    try {
      const accountData = await import('../../../services/accountService').then(module =>
        module.getAccountById(accountId)
      );
      console.log('Fetched account data for id:', accountId, 'Result:', accountData);
      return accountData;
    } catch (err) {
      console.error('Error fetching account by ID:', accountId, err);
      return null;
    }
  };

  const [formData, setFormData] = useState<Partial<PaymentMethod>>({
    name: '',
    fixedFees: 0,
    variableFees: 0,
    taxPercentage: 15, // القيمة الافتراضية للضريبة في المملكة
    linkedAccountId: '',
    linkedServiceProductId: '',
    authorizedUserIds: [] as string[],
    isActive: true
  });
  
  // إضافة وظيفة تحديد الحساب بطريقة أكثر استقرارًا
  // متغير حالة لتخزين بيانات الحساب المحدد بالكامل
  const [selectedAccountData, setSelectedAccountData] = useState<Account | null>(null);
  
  // وظيفة مبسطة لتحديد الحساب ومعلوماته
  const selectAccount = async (accountId: string, accountData?: any) => {
    if (!accountId) {
      console.error('Invalid account ID provided to selectAccount');
      setAccountStatus('error');
      return;
    }
    
    // تنظيف المعرف
    const cleanId = String(accountId).trim();
    console.log('Selecting account with cleaned ID:', cleanId);
    
    setAccountStatus('loading');
    setIsLoadingAccount(true);
    
    try {
      // حفظ بيانات الحساب الكاملة إذا توفرت
      if (accountData) {
        setSelectedAccountData(accountData);
        console.log('Setting complete account data:', accountData);
      } else {
        // البحث عن الحساب في القائمة
        const foundAccount = accounts.find(a => {
          const aId = a._id !== undefined ? String(a._id).trim() :
                    (a.id !== undefined ? String(a.id).trim() : '');
          return aId === cleanId;
        });
        
        if (foundAccount) {
          setSelectedAccountData(foundAccount);
          console.log('Found and set account data from list:', foundAccount);
        } else {
          console.warn('Account not found in loaded list, trying to fetch individually:', cleanId);
          try {
            // محاولة جلب بيانات الحساب بشكل مباشر من الخادم
            const fetchedAccount = await fetchAccountById(cleanId);
            if (fetchedAccount) {
              setSelectedAccountData(fetchedAccount);
              console.log('Successfully fetched account data:', fetchedAccount);
            } else {
              console.error('Failed to fetch account data for ID:', cleanId);
            }
          } catch (error) {
            console.error('Error fetching account:', error);
          }
        }
      }
      
      // تعيين المعرف مباشرة
      setSelectedAccountId(cleanId);
      
      // تحديث بيانات النموذج
      setFormData(prev => {
        const updated = {
          ...prev,
          linkedAccountId: cleanId
        };
        console.log('Updated form data with account ID:', updated);
        return updated;
      });
      
      // تحديث الحقل المخفي إذا كان موجوداً
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = cleanId;
      }
      
      setAccountStatus('loaded');
    } catch (error) {
      console.error('Error in selectAccount:', error);
      setAccountStatus('error');
    } finally {
      setIsLoadingAccount(false);
    }
  };
  
  // مراقبة البيانات الأولية وتحديث الحساب المرتبط إذا تغيرت
  useEffect(() => {
    if (initialData?.linkedAccountId && (!selectedAccountId || selectedAccountId !== initialData.linkedAccountId)) {
      console.log('Initial data changed, updating selected account:', initialData.linkedAccountId);
      selectAccount(initialData.linkedAccountId);
    }
  }, [initialData]);
  
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [serviceProducts, setServiceProducts] = useState<ServiceProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // تحميل البيانات الأولية
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        // جلب الحسابات النشطة فقط بدلاً من جميع الحسابات
        console.log('Fetching active accounts');
        const accountsData = await getActiveAccounts();
        console.log('Accounts data fetched:', accountsData);
        
        if (!accountsData || accountsData.length === 0) {
          console.log('No active accounts found, trying to get all accounts');
          // محاولة جلب جميع الحسابات كخطة بديلة
          const allAccountsData = await getAllAccounts();
          console.log('All accounts data fetched:', allAccountsData);
          setAccounts(Array.isArray(allAccountsData) ? allAccountsData : []);
        } else {
          setAccounts(Array.isArray(accountsData) ? accountsData : []);
        }
        // جلب المستخدمين
        try {
          const usersData = await getUsers();
          console.log('Users data fetched:', usersData);
          
          if (!usersData || usersData.length === 0) {
            console.log('No users found, trying to get active users specifically');
            // استخدام نهاية API صحيحة تحدد بوضوح أنها للبحث عن المستخدمين النشطين
            const activeUsersData = await fetch('/api/users?status=active').then(res => res.json());
            console.log('Active users data fetched:', activeUsersData);
            setUsers(Array.isArray(activeUsersData) ? activeUsersData : []);
          } else {
            setUsers(Array.isArray(usersData) ? usersData : []);
          }
        } catch (userErr) {
          console.error('Error fetching users:', userErr);
          setError('حدث خطأ أثناء تحميل بيانات المستخدمين');
        }
        // إزالة الاستدعاء المكرر لـ setUsers الذي يلغي الاستدعاء السابق
        
        // تعيين البيانات الأولية إذا كان في وضع التعديل
        if (initialData) {
          console.log('Setting initial data for editing mode:', initialData);
          
          // تحديد المستخدمين المصرح لهم من البيانات الأولية
          const authorizedIds = initialData.authorizedUserIds || [];
          
          console.log('Authorized user IDs from initial data:', authorizedIds);
          
          // حفظ معرف الحساب المرتبط للتعيين بعد تحميل الحسابات
          const accountIdToSelect = initialData.linkedAccountId || '';
          console.log('Account ID to select after loading accounts:', accountIdToSelect);
          
          setFormData({
            name: initialData.name,
            fixedFees: initialData.fixedFees,
            variableFees: initialData.variableFees,
            taxPercentage: initialData.taxPercentage,
            linkedAccountId: accountIdToSelect, // تخزين معرف الحساب مباشرة في النموذج
            // معالجة الحالة عندما يكون linkedServiceProductId كائناً مملوءاً أو نصاً
            linkedServiceProductId: initialData.linkedServiceProductId &&
                                     typeof initialData.linkedServiceProductId === 'object' ?
                                     initialData.linkedServiceProductId._id || initialData.linkedServiceProductId.id :
                                     (initialData.linkedServiceProductId || ''),
            authorizedUserIds: authorizedIds,
            isActive: initialData.isActive
          });
          
          // البحث عن الحساب المرتبط في قائمة الحسابات المحملة
          // تأخير البحث لوقت أطول للتأكد من تحميل الحسابات
          if (accountIdToSelect) {
            setTimeout(() => {
              console.log('Looking for account in loaded accounts after timeout');
              const foundAccount = accounts.find(a => {
                const aId = a._id !== undefined ? String(a._id).trim() :
                          (a.id !== undefined ? String(a.id).trim() : '');
                const matches = aId === accountIdToSelect;
                if (matches) {
                  console.log('Found matching account:', a);
                }
                return matches;
              });
              
              if (foundAccount) {
                console.log('Found account in loaded accounts list:', foundAccount);
                selectAccount(accountIdToSelect, foundAccount);
              } else {
                console.warn('Account not found in loaded accounts, trying to request it individually');
                // محاولة جلب الحساب بشكل منفصل إذا لم يتم العثور عليه في القائمة
                fetch(`/api/finance/accounts/${accountIdToSelect}`)
                  .then(res => res.json())
                  .then(accountData => {
                    if (accountData && (accountData._id || accountData.id)) {
                      console.log('Successfully fetched account individually:', accountData);
                      selectAccount(accountIdToSelect, accountData);
                    } else {
                      console.error('Received invalid account data from API:', accountData);
                    }
                  })
                  .catch(err => console.error('Failed to fetch account individually:', err));
              }
            }, 800); // زيادة وقت التأخير إلى 800 مللي ثانية
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching form data:', err);
        setError('حدث خطأ أثناء تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [initialData]);

  // جلب المنتجات الخدمية
  useEffect(() => {
    const fetchServiceProducts = async () => {
      try {
        const response = await getServiceProducts({ status: 'active', limit: 100 });
        if (response.success && response.data) {
          setServiceProducts(response.data);
        }
      } catch (err) {
        console.error('Error fetching service products:', err);
      }
    };
    fetchServiceProducts();
  }, []);

  // مراقبة صريحة للتغييرات في قائمة الحسابات
  useEffect(() => {
    // إذا كان هناك معرف حساب مرتبط ولكن لم يتم تحديد الحساب بعد
    if (formData.linkedAccountId && !selectedAccountData) {
      console.log('Account list changed, checking if we can find the linked account now');
      const accountId = formData.linkedAccountId;
      
      // البحث عن الحساب في القائمة المحدثة
      const foundAccount = accounts.find(a => {
        const aId = a._id !== undefined ? String(a._id).trim() :
                  (a.id !== undefined ? String(a.id).trim() : '');
        return aId === accountId;
      });
      
      if (foundAccount) {
        console.log('Found linked account after accounts list updated:', foundAccount);
        selectAccount(accountId, foundAccount);
      }
    }
  }, [accounts]);
  
  // التحقق من صحة البيانات
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.name?.trim()) {
      errors.name = 'اسم طريقة الدفع مطلوب';
    }
    
    if (!formData.linkedAccountId) {
      errors.linkedAccountId = 'الحساب المرتبط مطلوب';
    }
    
    if (formData.fixedFees === undefined || formData.fixedFees < 0) {
      errors.fixedFees = 'يجب أن تكون الرسوم الثابتة صفر أو أكبر';
    }
    
    if (formData.variableFees === undefined || formData.variableFees < 0 || formData.variableFees > 100) {
      errors.variableFees = 'يجب أن تكون الرسوم المتغيرة بين 0 و 100';
    }
    
    if (formData.taxPercentage === undefined || formData.taxPercentage < 0 || formData.taxPercentage > 100) {
      errors.taxPercentage = 'يجب أن تكون نسبة الضريبة بين 0 و 100';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // معالجة تغيير الحقول
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    
    // طريقة تحديث مباشرة للنموذج
    const updatedValue = type === 'number' ? parseFloat(value) : value;
    console.log(`Setting ${name} to ${updatedValue}`);
    
    // استخدام طريقة مختلفة لتحديث الحالة
    const updatedForm = { ...formData };
    updatedForm[name] = updatedValue;
    setFormData(updatedForm);
    console.log('Updated form data:', updatedForm);
    
    // مسح خطأ التحقق عند التعديل
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // معالجة تغيير القائمة المنسدلة
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value;
    
    if (!name) return;
    
    console.log(`Select change - name: ${name}, value: ${value}`);
    
    // تحديث مباشر لقيمة الحساب المرتبط
    if (name === 'linkedAccountId') {
      console.log(`Setting linkedAccountId to: ${value}`);
    }
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      console.log('Updated form data:', updated);
      return updated;
    });
    
    // مسح خطأ التحقق عند التعديل
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // معالجة تغيير المستخدمين المصرح لهم
  const handleAuthorizedUsersChange = (_: React.SyntheticEvent, value: User[]) => {
    console.log('Selected users:', value);
    const userIds = value.map(user => user._id);
    console.log('Updated authorizedUserIds:', userIds);
    
    // تحديث formData بالمستخدمين المحددين
    setFormData(prev => {
      const updated = {
        ...prev,
        authorizedUserIds: userIds
      };
      console.log('Updated form data with users:', updated);
      return updated;
    });
  };
  
  // معالجة تغيير حالة التفعيل
  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      isActive: e.target.checked
    }));
  };

  // معالجة تغيير المنتج الخدمي المرتبط
  const handleServiceProductChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      linkedServiceProductId: value || undefined
    }));
  };
  
  // حفظ النموذج
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Submitting form with data:', formData);
    console.log('Selected account ID:', selectedAccountId);
    
    // التحقق من صحة البيانات
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      // التأكد من صحة قيمة الحساب المرتبط قبل الإرسال
      if (!selectedAccountId) {
        console.error('No account selected!');
        setValidationErrors(prev => ({
          ...prev,
          linkedAccountId: 'الحساب المرتبط مطلوب'
        }));
        setSubmitting(false);
        return;
      }
      
      // إنشاء نسخة من بيانات النموذج لتجنب مشاكل التزامن (asynchronous)
      // وضمان استخدام آخر قيمة محدثة للحساب المرتبط
      const updatedFormData = {
        ...formData,
        linkedAccountId: selectedAccountId, // استخدام أحدث قيمة للحساب المرتبط
        linkedServiceProductId: formData.linkedServiceProductId || undefined
      };
      
      console.log('Updated form data with latest account ID:', updatedFormData);
      
      // تأكيد من وجود المستخدمين المحددين
      console.log('Authorized users to be saved:', formData.authorizedUserIds);
      
      // استخدام بيانات الحساب المحدد المخزنة بدلاً من البحث عنها
      console.log('Using stored selected account data:', selectedAccountData);
      
      // تحضير البيانات للإرسال للخادم
      const dataToSubmit = {
        ...updatedFormData,
        // تأكد من وجود قائمة المستخدمين المصرح لهم
        authorizedUserIds: updatedFormData.authorizedUserIds || [],
        // إضافة معلومات الحساب للسجل فقط، لن يتم إرسالها للخادم
        _debug_accountName: selectedAccountData?.name || 'غير معروف'
      };
      
      console.log('Submitting data with authorized users:', dataToSubmit);
      console.log('Using selected account ID:', selectedAccountId);
      console.log('Selected account name:', selectedAccountData?.name || 'اسم الحساب غير متوفر');
      
      if (isEditing && initialData) {
        // تحديث طريقة الدفع الحالية
        console.log(`Updating payment method with ID: ${initialData.id}`);
        const result = await paymentMethodService.updatePaymentMethod(initialData.id, dataToSubmit);
        console.log('Update payment method result:', result);
      } else {
        // إنشاء طريقة دفع جديدة
        await paymentMethodService.createPaymentMethod(dataToSubmit);
      }
      
      console.log('Payment method saved successfully with users:', formData.authorizedUserIds);
      // عرض رسالة نجاح بدون التركيز على عدد المستخدمين
      alert('تم حفظ طريقة الدفع بنجاح');
      onSuccess();
      setError(null);
    } catch (err) {
      console.error('Error saving payment method:', err);
      setError('حدث خطأ أثناء حفظ طريقة الدفع');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom color="primary" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 2
        }}>
          <AccountBalanceIcon fontSize="small" />
          المعلومات الأساسية
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="اسم طريقة الدفع"
              name="name"
              value={formData.name || ''}
              onChange={handleInputChange}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!validationErrors.linkedAccountId} required>
              <InputLabel id="linkedAccountId-label">الحساب المالي المرتبط</InputLabel>
              <Select
                labelId="linkedAccountId-label"
                id="linkedAccountId"
                name="linkedAccountId"
                value={selectedAccountId}
                label="الحساب المالي المرتبط"
                onChange={(e) => {
                  const accountId = e.target.value;
                  if (!accountId) return;
                  
                  // العثور على بيانات الحساب الكاملة
                  const selectedAccount = accounts.find(a => {
                    const aId = a._id !== undefined ? String(a._id).trim() :
                             (a.id !== undefined ? String(a.id).trim() : '');
                    return aId === accountId;
                  });
                  
                  if (selectedAccount) {
                    // تحديث حالة الحساب وبياناته
                    selectAccount(accountId, selectedAccount);
                  }
                }}
                error={!!validationErrors.linkedAccountId}
              >
                <MenuItem value="">
                  <em>اختر الحساب</em>
                </MenuItem>
                {accounts.map(account => {
                  let accountId = '';
                  
                  if (account._id !== undefined && account._id !== null) {
                    accountId = String(account._id).trim();
                  } else if (account.id !== undefined && account.id !== null) {
                    accountId = String(account.id).trim();
                  } else {
                    accountId = `temp-${account.name}-${Date.now()}`;
                  }
                  
                  return (
                    <MenuItem key={accountId} value={accountId}>
                      {account.name}
                      {account.accountNumber && ` (${account.accountNumber})`}
                    </MenuItem>
                  );
                })}
              </Select>
              {validationErrors.linkedAccountId && (
                <FormHelperText error>{validationErrors.linkedAccountId}</FormHelperText>
              )}
              {accounts.length === 0 && (
                <FormHelperText>لم يتم العثور على حسابات مالية. الرجاء إنشاء حساب مالي أولاً.</FormHelperText>
              )}
              {isLoadingAccount && (
                <Box mt={1} display="flex" alignItems="center">
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="caption">جارِ تحميل بيانات الحساب...</Typography>
                </Box>
              )}
              {selectedAccountId && selectedAccountData && (
                <Box mt={1} p={1} bgcolor="#f0f8ff" borderRadius={1}>
                  <Typography variant="body2" color="primary">
                    ✓ تم اختيار: {selectedAccountData.name}
                    {selectedAccountData.accountNumber && ` (${selectedAccountData.accountNumber})`}
                  </Typography>
                </Box>
              )}
              {accountStatus === 'error' && (
                <Alert severity="error" sx={{ mt: 1, p: 0.5 }}>
                  <Typography variant="caption">حدث خطأ أثناء تحميل بيانات الحساب</Typography>
                </Alert>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="linkedServiceProductId-label">المنتج الخدمي المرتبط (اختياري)</InputLabel>
              <Select
                labelId="linkedServiceProductId-label"
                id="linkedServiceProductId"
                name="linkedServiceProductId"
                value={formData.linkedServiceProductId || ''}
                label="المنتج الخدمي المرتبط (اختياري)"
                onChange={handleServiceProductChange}
              >
                <MenuItem value="">
                  <em>بدون منتج خدمي</em>
                </MenuItem>
                {serviceProducts.map(product => (
                  <MenuItem key={product._id} value={product._id}>
                    {product.name} {product.barcode && `(${product.barcode})`}
                  </MenuItem>
                ))}
              </Select>
              {serviceProducts.length === 0 && (
                <FormHelperText>لم يتم العثور على منتجات خدمية</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive ?? true}
                  onChange={handleActiveChange}
                  color="primary"
                />
              }
              label="مفعلة"
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom color="primary" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 2
        }}>
          <MoneyIcon fontSize="small" />
          الرسوم والضرائب
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="الرسوم الثابتة"
              name="fixedFees"
              type="number"
              value={formData.fixedFees ?? 0}
              onChange={handleInputChange}
              error={!!validationErrors.fixedFees}
              helperText={validationErrors.fixedFees}
              InputProps={{
                endAdornment: <InputAdornment position="end">ريال</InputAdornment>,
                inputProps: { min: 0, step: 0.01 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="الرسوم المتغيرة"
              name="variableFees"
              type="number"
              value={formData.variableFees ?? 0}
              onChange={handleInputChange}
              error={!!validationErrors.variableFees}
              helperText={validationErrors.variableFees}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, max: 100, step: 0.01 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="نسبة الضريبة"
              name="taxPercentage"
              type="number"
              value={formData.taxPercentage ?? 15}
              onChange={handleInputChange}
              error={!!validationErrors.taxPercentage}
              helperText={validationErrors.taxPercentage}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, max: 100, step: 0.01 }
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom color="primary" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 2
        }}>
          <PeopleIcon fontSize="small" />
          المستخدمون المصرح لهم
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={users || []}
              getOptionLabel={(option) => option.name || option.fullName || option.username || 'بدون اسم'}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={(users || []).filter(user =>
                formData.authorizedUserIds &&
                Array.isArray(formData.authorizedUserIds) &&
                formData.authorizedUserIds.includes(user._id)
              )}
              onChange={handleAuthorizedUsersChange}
              noOptionsText="لا يوجد مستخدمين"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="المستخدمون المصرح لهم"
                  placeholder="اختر المستخدمين المصرح لهم باستخدام طريقة الدفع"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name || option.fullName || option.username || 'بدون اسم'}
                    {...getTagProps({ index })}
                    key={option._id}
                    color="primary"
                  />
                ))
              }
            />
            {users.length === 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                لم يتم العثور على مستخدمين. تأكد من وجود مستخدمين في النظام.
              </Alert>
            )}
            <Alert severity="info" sx={{ mt: 1 }}>
              تم اختيار <strong>{formData.authorizedUserIds?.length || 0}</strong> مستخدم مصرح له
            </Alert>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              * إذا لم يتم اختيار أي مستخدم، فسيكون جميع المستخدمين مصرح لهم باستخدام طريقة الدفع
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={submitting}
        >
          إلغاء
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={submitting}
        >
          {isEditing ? 'حفظ التعديلات' : 'إضافة طريقة الدفع'}
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentMethodForm;